import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from 'ol/style'
import VectorImage from 'ol/layer/VectorImage'
import heatMap from '../../../../utils/constants/heatMap'

const activeFill = new Fill({
    color: 'rgb(50, 115, 220)',
})

const activeStroke = new Stroke({
    color: '#ffffff',
    width: 1,
})

const inactiveFill = new Fill({
    color: '#9C9C9C',
})

// const inactiveStroke = new Stroke({
//     color: 'rgb(0, 100, 255)',
//     width: 1,
// })

const getDataIntervalFromRange = (range, numBins) => {
    const [dataMin, dataMax] = range
    const diff = dataMax - dataMin
    const interval = diff / numBins
    return interval
}

const getLayerLegendObject = (dataLayerName, legendStyles, groupName, symbol = 'circle') => ({
    [dataLayerName]: {
        display: false,
        expanded: false,
        unique: true,
        LayerName: dataLayerName,
        layerGroupName: groupName,
        loaded: true,
        shape: symbol,
        StyleArray: JSON.stringify(legendStyles),
        LayerType: 'THEME'
    }
})

const activeStyle = { stroke: activeStroke, fill: activeFill }
const inactiveStyle = { fill: inactiveFill }
const styles = {
    1: {
        1: new Style({
            image: new CircleStyle({
                radius: 3,
                ...activeStyle,
            }),
            zIndex: 10
        }),
        2: new Style({
            image: new CircleStyle({
                radius: 4,
                ...activeStyle,
            }),
            zIndex: 10
        }),
        3: new Style({
            image: new CircleStyle({
                radius: 5,
                ...activeStyle,
            }),
            zIndex: 10
        }),
        4: new Style({
            image: new CircleStyle({
                radius: 6,
                ...activeStyle,
            }),
            zIndex: 10
        }),
        5: new Style({
            image: new CircleStyle({
                radius: 7,
                ...activeStyle,
            }),
            zIndex: 10
        }),
        6: new Style({
            image: new CircleStyle({
                radius: 8,
                ...activeStyle,
            }),
            zIndex: 10
        }),
        7: new Style({
            image: new CircleStyle({
                radius: 9,
                ...activeStyle,
            }),
            zIndex: 10
        }),
    },
    0: {
        1: new Style({
            image: new CircleStyle({
                radius: 1,
                ...inactiveStyle,
            }),
        }),
        2: new Style({
            image: new CircleStyle({
                radius: 2,
                ...inactiveStyle,
            }),
        }),
        3: new Style({
            image: new CircleStyle({
                radius: 3,
                ...inactiveStyle,
            }),
        }),
        4: new Style({
            image: new CircleStyle({
                radius: 4,
                ...inactiveStyle,
            }),
        }),
        5: new Style({
            image: new CircleStyle({
                radius: 5,
                ...inactiveStyle,
            }),
        }),
        6: new Style({
            image: new CircleStyle({
                radius: 6,
                ...inactiveStyle,
            }),
        }),
        7: new Style({
            image: new CircleStyle({
                radius: 7,
                ...inactiveStyle,
            }),
        }),
    },
    invisible: new Style({
        image: new CircleStyle({
            stroke: null,
            fill: null,
        }),
    }),
    highlighted: new Style({
        image: new CircleStyle({
            stroke: new Stroke({ color: 'rgb(100, 80, 210)', width: 2 }),
            fill: new Fill({ color: 'rgb(210, 80, 210)' }),
            radius: 5,
        }),
        zIndex: 12,
    }),
}

const sizes = [1,2,3,4,5,6,7]

const drawStyles = {
    polygonStyles: new Style({
        stroke: new Stroke({color: 'rgb(235, 162, 80)', width: 2}),
        fill: new Fill({color: 'rgba(235, 162, 80, 0.3)'}),
    }),
    pointerStylesHighlighted: new Style ({
        image: new CircleStyle({
            ...activeStyle,
            radius: 7,
        })
    }),
    pointerStylesSelected: new Style ({
        image: new CircleStyle({
            ...inactiveStyle,
            radius: 7,
        })
    }),
    boundingBoxStyles: new Style({
        stroke: new Stroke({color: 'rgb(235, 162, 80)'}),
    }),
}

const sampleTypePerFacilityLegend = [
    { range: 'Chem Samples', fillColor: '#0000FF' }, 
    { range: 'Bio Samples', fillColor: '#FF9900' }, 
    { range: 'Phys Samples', fillColor: '#04B152' },
    { range: 'Chem-Bio Samples', fillColor: '#0000FF', strokeColor: '#FF9900', strokeWidth: 2 },
    { range: 'Chem-Phys Samples', fillColor: '#0000FF', strokeColor: '#04B152', strokeWidth: 2 },
    { range: 'Bio-Phys Samples', fillColor: '#FF9900', strokeColor: '#04B152', strokeWidth: 2 },
    { range: 'Chem-Bio-Phys Samples', fillColor: 'red' },
]

const sampleTypePerFacilityStyles = sampleTypePerFacilityLegend.reduce((acc, curr) => {
    const color = curr.fillColor
    const strokeColor = curr.strokeColor
    const strokeWidth = curr.strokeWidth 
    const options = sizes.reduce((sizeOptions, size) => {
        return {
            ...sizeOptions,
            [size]: new Style({
                image: new CircleStyle({
                    fill: new Fill({
                        color
                    }),
                    radius: size,
                    stroke: new Stroke({
                        color: strokeColor,
                        width: strokeWidth,
                    }),
                })
            })
        }
    }, {})
    return {
        ...acc,
        [curr.range]: options
    }
}, {})

const waterBodyLegend = [
    { fillColor: 'blue', range: 'Streams' },
    { fillColor: 'orange', range: 'Lakes' },
    { fillColor: 'grey', range: 'Unspecified' },
]

const waterBodyStyles = waterBodyLegend.reduce((acc, curr) => {
    const color = curr.fillColor
    const options = sizes.reduce((sizeOptions, size) => {
        return {
            ...sizeOptions,
            [size]: new Style({
                image: new CircleStyle({
                    fill: new Fill({
                        color
                    }),
                    radius: size, 
                    stroke: new Stroke({
                        color: 'black',
                        width: 0.2 * size,
                    }),
                })
            })
        }
    }, {})
    return {
        ...acc,
        [curr.range]: options
    }
}, {})

const getRangeBasedLegendObject = (range, selectedHeatMap, appendUnthemedLegendItem = false, unthemedLegendMessage = 'No Data') => {
    const targetHeatMap = selectedHeatMap ? selectedHeatMap : heatMap
    let [dataMin] = range
    const unthemedLegendItemOffset = appendUnthemedLegendItem ? 1 : 0 
    const interval = getDataIntervalFromRange(range, targetHeatMap.length)
    if (interval === NaN || interval === 0 ) {
        return [{
            color: targetHeatMap[0],
            range: `${dataMin}`,
            dataMin: dataMin,
            dataMax: null
        }]
    }
    const proposedHeatMap = targetHeatMap.map((color, idx) => {
        let min = Math.ceil((dataMin + (idx * interval)) + unthemedLegendItemOffset)
        // if (min === 0 && appendUnthemedLegendItem) {
        //     min = 1 //start list from 1 if there is a default 'no data' item in the legend
        // }
        const max = Math.ceil((dataMin + ((idx + 1) * interval)))
        return {
            fillColor: color,
            range: `${min} - ${max}`,
            dataMin: min,
            dataMax: max,
        }
    })//.filter(x => x.dataMax - x.dataMin > 0)//WSW-606 We don't want intervals like 50-50 or 2-2 or 14-14
    
    if (appendUnthemedLegendItem) {
        const defaultUnthemedStyle = {
            fillColor: 'grey',
            range: unthemedLegendMessage,
            dataMin: 0,
            dataMax: null,
        }
        return [defaultUnthemedStyle, ...proposedHeatMap]
    }
    return proposedHeatMap
}

const getMapStyleObject = (legendArray) => legendArray.reduce((acc, curr) => {
    const color = curr.fillColor
    const options = sizes.reduce((sizeOptions, size) => {
        return {
            ...sizeOptions,
            [size]: new Style({
                image: new CircleStyle({
                    fill: new Fill({
                        color
                    }),
                    radius: size, 
                    stroke: new Stroke({
                        color: 'black',
                        width: 0.2 * size,
                    }),
                })
            })
        }
    }, {})
    return {
        ...acc,
        [curr.range]: options
    }
}, {})

const unthemedStyles = sizes.reduce((acc, curr) => {
    return {
        ...acc,
        [curr]: new Style({
            image: new CircleStyle({
                fill: new Fill({
                    color: 'grey'
                }),
                radius: curr
            })
        })
    }
}, {})

const styleFunction = function(theme, feature, zoomLevel, styleObject) {
    const size = Math.ceil(zoomLevel)
    try {
        const selected = feature.get('selected')
        const displayed = feature.get('displayed')
        const highlighted = feature.get('highlighted')
        if (displayed) {
            // console.log(size);
            
            let selectedStyle = 0 
            if (highlighted) {
                return styles['highlighted']
            }
            if (selected) {
                return styles[selected][size]
            } else {
                const hasChem = feature.get('ChemistrySampleCount') > 0
                const hasBio = feature.get('BiologicalSampleCount') > 0
                const hasPhys = feature.get('PhysicalSampleCount') > 0
                if (theme === 'sampleTypePerFacility') {
                    selectedStyle = 
                        hasChem && !hasBio && !hasPhys ? 'Chem Samples' :
                        hasBio && !hasChem && !hasPhys ? 'Bio Samples' :
                        hasPhys && !hasChem && !hasBio ? 'Phys Samples' :
                        hasChem && hasBio && !hasPhys ? 'Chem-Bio Samples' :
                        hasChem && hasPhys && !hasBio ? 'Chem-Phys Samples' :
                        hasBio && hasPhys && !hasChem ? 'Bio-Phys Samples' :
                        hasChem && hasBio && hasPhys ? 'Chem-Bio-Phys Samples' : 'Chem Samples'
                    return sampleTypePerFacilityStyles[selectedStyle][size]
                } else if (theme === `waterBodyType`) {
                    const facilityType = feature.get('FacilityType')
                    selectedStyle = 
                        facilityType && (facilityType === 'R' || facilityType === 'S') ? 'Streams' :
                        facilityType && facilityType === 'UN' ? 'Unspecified' : 'Lakes'
                    return waterBodyStyles[selectedStyle][size]
                } else if (theme === 'chemHistory') {
                    const count = feature.get('ChemistrySampleCountFiveYears')
                    let rangeKey
                    if (count === 0 || count === undefined) {
                        return unthemedStyles[size]
                    }
                    for(let range in styleObject) {
                        let [min, max, ...others] = range.split('-')
                        min = Number(min)
                        max = Number(max)
                        if (count > 0 && count >= min && count <= max) {
                            rangeKey = range
                        }
                    }
                    return styleObject[rangeKey][size]
                } else if (theme === 'bioHistory' && hasBio) {
                    const count = feature.get('BiologicalSampleCountFiveYears')
                    let rangeKey
                    if (count === 0 || count === undefined) {
                        return unthemedStyles[size]
                    }
                    for(let range in styleObject) {
                        let [min, max, ...others] = range.split('-')
                        min = Number(min)
                        max = Number(max)
                        if (count > 0 && count >= min && count <= max) {
                            rangeKey = range
                        }
                    }
                    return styleObject[rangeKey][size]
                } else {
                    return unthemedStyles[size]
                }
            }
        }
        return styles.invisible[0]        
    } catch (e) {
        return styles.invisible[0] 
    }
}

export {
    sampleTypePerFacilityLegend,
    waterBodyLegend,
    drawStyles,
    getLayerLegendObject,
    getDataIntervalFromRange,
    getRangeBasedLegendObject,
    getMapStyleObject,
}

export default styleFunction
